import React, { useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import { API_CONFIG } from '@/config/api';

interface AuthTestResult {
  test: string;
  status: 'success' | 'error' | 'warning';
  message: string;
  details?: any;
}

export const AuthTester: React.FC = () => {
  const { user, token, isAuthenticated, isLoading, logout } = useAuthStore();
  const [testResults, setTestResults] = useState<AuthTestResult[]>([]);
  const [isRunningTests, setIsRunningTests] = useState(false);

  const runAuthTests = async () => {
    setIsRunningTests(true);
    const results: AuthTestResult[] = [];

    // Test 1: Check authentication state
    results.push({
      test: 'Authentication State',
      status: isAuthenticated ? 'success' : 'error',
      message: isAuthenticated 
        ? `✅ Authenticated as ${user?.email}` 
        : '❌ Not authenticated (this should not happen on this page)',
      details: { 
        user: user ? {
          id: user.id,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role
        } : null, 
        token: token ? 'Present' : 'Missing', 
        isAuthenticated 
      }
    });

    // Test 2: Check localStorage tokens
    const storedToken = localStorage.getItem('auth_token');
    const storedUserId = localStorage.getItem('user_id');
    results.push({
      test: 'Local Storage Tokens',
      status: storedToken && storedUserId ? 'success' : 'warning',
      message: storedToken && storedUserId 
        ? '✅ Auth tokens found in localStorage' 
        : '⚠️ Missing auth tokens in localStorage',
      details: { 
        token: storedToken ? 'Present' : 'Missing',
        userId: storedUserId ? 'Present' : 'Missing'
      }
    });

    // Test 3: Test Cloudflare Functions availability
    try {
      const baseUrl = window.location.origin;
      const healthResponse = await fetch(`${baseUrl}/auth/health`, { method: 'GET' });
      
      results.push({
        test: 'Cloudflare Functions',
        status: healthResponse.ok ? 'success' : 'warning',
        message: healthResponse.ok 
          ? '✅ Cloudflare Functions accessible' 
          : `⚠️ Cloudflare Functions not available (${healthResponse.status})`,
        details: { status: healthResponse.status, statusText: healthResponse.statusText }
      });
    } catch (error) {
      results.push({
        test: 'Cloudflare Functions',
        status: 'error',
        message: '❌ Cloudflare Functions not accessible',
        details: { error: error instanceof Error ? error.message : 'Unknown error' }
      });
    }

    // Test 4: Test backend user sync (if authenticated)
    if (storedToken && storedUserId) {
      try {
        const syncResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL || API_CONFIG.BASE_URL}/api/v1/auth/users/sync`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${storedToken}`,
            'Origin': window.location.origin,
          },
          body: JSON.stringify({
            id: storedUserId,
            email: user?.email || 'unknown@example.com',
            firstName: user?.firstName || 'Unknown',
            lastName: user?.lastName || 'User',
            role: user?.role?.toLowerCase() || 'customer'
          })
        });

        results.push({
          test: 'Backend User Sync',
          status: syncResponse.ok ? 'success' : 'warning',
          message: syncResponse.ok 
            ? '✅ User sync to backend successful' 
            : `⚠️ User sync failed (${syncResponse.status})`,
          details: { 
            status: syncResponse.status, 
            statusText: syncResponse.statusText,
            endpoint: '/api/v1/auth/users/sync'
          }
        });
      } catch (error) {
        results.push({
          test: 'Backend User Sync',
          status: 'error',
          message: '❌ User sync network error',
          details: { error: error instanceof Error ? error.message : 'Unknown error' }
        });
      }
    }

    // Test 5: Test JWT token validation with backend
    if (storedToken) {
      try {
        const tokenResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : 'https://rapidfix.ie')}/api/v1/users/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${storedToken}`,
            'Content-Type': 'application/json',
            'Origin': window.location.origin,
          }
        });

        results.push({
          test: 'JWT Token Validation',
          status: tokenResponse.ok ? 'success' : tokenResponse.status === 401 ? 'error' : 'warning',
          message: tokenResponse.ok 
            ? '✅ JWT token valid and accepted by backend'
            : tokenResponse.status === 401 
              ? '❌ JWT token rejected (invalid or expired)'
              : `⚠️ Token validation failed (${tokenResponse.status})`,
          details: { 
            status: tokenResponse.status, 
            statusText: tokenResponse.statusText,
            endpoint: '/api/v1/users/profile'
          }
        });
      } catch (error) {
        results.push({
          test: 'JWT Token Validation',
          status: 'error',
          message: '❌ Token validation network error',
          details: { error: error instanceof Error ? error.message : 'Unknown error' }
        });
      }
    }

    // Test 6: Test session validation
    try {
      const baseUrl = window.location.origin;
      const sessionResponse = await fetch(`${baseUrl}/auth/session`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${storedToken}`,
          'Content-Type': 'application/json',
        }
      });
      
      results.push({
        test: 'Session Validation',
        status: sessionResponse.ok ? 'success' : 'warning',
        message: sessionResponse.ok 
          ? '✅ Session valid with Cloudflare Functions' 
          : `⚠️ Session validation failed (${sessionResponse.status})`,
        details: { 
          status: sessionResponse.status, 
          statusText: sessionResponse.statusText,
          endpoint: '/auth/session'
        }
      });
    } catch (error) {
      results.push({
        test: 'Session Validation',
        status: 'error',
        message: '❌ Session validation network error',
        details: { error: error instanceof Error ? error.message : 'Unknown error' }
      });
    }

    setTestResults(results);
    setIsRunningTests(false);
  };

  const testLogout = async () => {
    try {
      setIsRunningTests(true);
      await logout();
      
      setTestResults(prev => [...prev, {
        test: 'Test Logout',
        status: 'success',
        message: '✅ Logout successful - you will be redirected',
        details: {}
      }]);
      
      // Redirect after logout
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    } catch (error) {
      setTestResults(prev => [...prev, {
        test: 'Test Logout',
        status: 'error',
        message: `❌ Logout failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        details: { error }
      }]);
    } finally {
      setIsRunningTests(false);
    }
  };

  const getStatusIcon = (status: 'success' | 'error' | 'warning') => {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
    }
  };

  const getStatusColor = (status: 'success' | 'error' | 'warning') => {
    switch (status) {
      case 'success': return 'text-green-600';
      case 'error': return 'text-red-600';
      case 'warning': return 'text-yellow-600';
    }
  };

  const successCount = testResults.filter(r => r.status === 'success').length;
  const errorCount = testResults.filter(r => r.status === 'error').length;
  const warningCount = testResults.filter(r => r.status === 'warning').length;

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          🔐 Authentication System Tester
        </h2>
        <p className="text-gray-600">
          Test authentication flow, token validation, and backend integration using your current session.
        </p>
      </div>

      {/* Current Auth State */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-2">Current Authentication State</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium">Status:</span> 
            <span className={`ml-2 ${isAuthenticated ? 'text-green-600' : 'text-red-600'}`}>
              {isAuthenticated ? '✅ Authenticated' : '❌ Not Authenticated'}
            </span>
          </div>
          <div>
            <span className="font-medium">Loading:</span> 
            <span className={`ml-2 ${isLoading ? 'text-yellow-600' : 'text-gray-600'}`}>
              {isLoading ? '⏳ Loading...' : '✅ Ready'}
            </span>
          </div>
          <div>
            <span className="font-medium">User:</span> 
            <span className="ml-2 text-gray-700">
              {user ? `${user.firstName} ${user.lastName} (${user.email})` : 'None'}
            </span>
          </div>
          <div>
            <span className="font-medium">Role:</span> 
            <span className="ml-2 text-gray-700">
              {user?.role || 'None'}
            </span>
          </div>
        </div>
      </div>

      {/* Test Controls */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-wrap gap-3">
          <button
            onClick={runAuthTests}
            disabled={isRunningTests}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              isRunningTests
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {isRunningTests ? '⏳ Testing...' : '🧪 Run Auth Tests'}
          </button>

          <button
            onClick={testLogout}
            disabled={isRunningTests}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              isRunningTests
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700 text-white'
            }`}
          >
            🚪 Test Logout
          </button>
        </div>

        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>🔒 Secure Testing:</strong> All tests use your current authenticated session. 
            No credentials are stored or displayed on this page.
          </p>
        </div>
      </div>

      {/* Test Results */}
      {testResults.length > 0 && (
        <div className="space-y-4">
          {/* Summary */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-2">📊 Test Summary</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-green-600">{successCount}</div>
                <div className="text-sm text-gray-600">Passed</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-yellow-600">{warningCount}</div>
                <div className="text-sm text-gray-600">Warnings</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">{errorCount}</div>
                <div className="text-sm text-gray-600">Failed</div>
              </div>
            </div>
          </div>

          {/* Detailed Results */}
          <div>
            <h3 className="text-lg font-semibold mb-4">🔍 Detailed Results</h3>
            <div className="space-y-3">
              {testResults.map((result, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <span className="text-lg">{getStatusIcon(result.status)}</span>
                      <span className="font-medium">{result.test}</span>
                    </div>
                  </div>
                  <div className={`text-sm ${getStatusColor(result.status)} mb-2`}>
                    {result.message}
                  </div>
                  {result.details && (
                    <details className="text-xs text-gray-600">
                      <summary className="cursor-pointer hover:text-gray-800">Show Details</summary>
                      <pre className="mt-2 p-2 bg-gray-100 rounded overflow-x-auto">
                        {JSON.stringify(result.details, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Troubleshooting */}
          {errorCount > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-semibold text-red-800 mb-2">🔧 Troubleshooting Authentication Issues</h4>
              <ul className="text-sm text-red-700 space-y-1">
                <li>• Check if Cloudflare Functions are deployed and accessible</li>
                <li>• Verify backend services are running and accepting JWT tokens</li>
                <li>• Check browser console for detailed error messages</li>
                <li>• Verify CORS configuration allows authentication requests</li>
                <li>• Try logging out and back in to refresh your session</li>
                <li>• Ensure your session hasn't expired</li>
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Instructions */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-800 mb-2">📋 How to Use</h4>
        <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
          <li>Click "Run Auth Tests" to test your current authentication session</li>
          <li>Review test results to identify any authentication issues</li>
          <li>Use "Test Logout" to verify logout functionality (will redirect you)</li>
          <li>Check browser console for detailed logs and error messages</li>
          <li>All tests use your current session - no credentials needed</li>
        </ol>
      </div>
    </div>
  );
};

export default AuthTester;